import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import '../../assets/css/8dreport.css';
import { formatDate, getDateFormat } from '../../utils/dateUtils';
import { LocaleContext } from '../../components/LocaleContext';
import AutoResizeTextArea from './AutoResizeTextArea';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import { resolvePrintCompanyLogoSrc } from '../../utils/companyBranding';
import {
  buildFooterCompanyLinesFromCsp,
  formatPrintToday,
  printFooterPlainText,
} from '../../utils/printChrome';
import { otkReportActionLabelKey } from '../../utils/otkErrorRecordReportLabel';

function absLogoUrl(logoPath) {
  const t = (logoPath || '').trim();
  if (!t) return null;
  if (/^https?:\/\//i.test(t)) return t;
  return null;
}

const EMPTY_ERROR_RECORD = {
  type: 'Interná chyba',
  article_number: '',
  article_name: '',
  order_number: '',
  customer_id: '',
  offender_id: '',
  repair_costs: '',
  total_amount: '',
  defective_pieces: '',
  group_id: '',
  ack_error: '',
  claim_counterparty_number: '',
  error_type_id: '',
  contractor_note: '',
  record_processor_id: '',
  problem_description: '',
  main_reason: '',
  immediate_measure: '',
  long_term_measure: '',
  prevention_id: '',
  auditor_id: '',
  auditor_note: '',
  state: 'Otvorená',
  created_at: '',
  id: '',
  specific_id: '',
};

export default function EightDreport({ error_record }) {
  const [state] = useContext(DatabaseContext);
  const { locale, t } = useContext(LocaleContext);

  const csp = state.company_settings_print;
  const branding = state.branding;

  const formErrorRecord = error_record ?? EMPTY_ERROR_RECORD;

  const useUnifiedHeader = !!csp?.use_unified_header;
  const useUnifiedFooter = !!csp?.use_unified_footer;

  const remoteLogo = useMemo(() => {
    const fromBranding = resolvePrintCompanyLogoSrc(branding, '');
    if (fromBranding) return absLogoUrl(fromBranding) || fromBranding;
    return absLogoUrl(csp?.logo_path);
  }, [branding, csp?.logo_path]);
  const [logoRemoteFailed, setLogoRemoteFailed] = useState(false);

  useEffect(() => {
    setLogoRemoteFailed(false);
  }, [remoteLogo]);

  const showRemoteLogo = Boolean(useUnifiedHeader && remoteLogo && !logoRemoteFailed);
  // Non-unified 8D header keeps PRODUCT fallback; company logo only when unified header is on.
  const logoSrc = showRemoteLogo ? remoteLogo : defaultProductLogoSrc;

  const printDateStr = useMemo(
    () => formatPrintToday(state?.module_settings),
    [state?.module_settings]
  );

  const footerUnifiedBody = useUnifiedFooter ? printFooterPlainText(csp || {}) : '';
  const footerCompany = useMemo(() => buildFooterCompanyLinesFromCsp(csp || null), [csp]);
  const footerPlainWhenNotUnified = printFooterPlainText(csp || {});
  const hasCspFooterContent = Boolean(
    csp &&
      (footerCompany.companyName ||
        footerCompany.location ||
        footerCompany.taxLine ||
        footerCompany.contactLine ||
        footerCompany.bankLine ||
        footerPlainWhenNotUnified)
  );

  const reportTitle = t(otkReportActionLabelKey(formErrorRecord.type));

  return (
    <>
      <div className="relative min-h-screen flex flex-col justify-center items-center overflow-x">
        <main className="flex-1">
          <div
            className={`w-fit p-6 bg-white ${useUnifiedHeader && csp ? 'border-t-4 border-t-zinc-700' : ''} ${
              useUnifiedFooter && csp ? 'border-b-4 border-b-zinc-700' : ''
            }`}
          >
            {useUnifiedHeader && csp ? (
              <>
                <header className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,180px)_1fr_minmax(0,280px)] sm:items-start border-b border-gray-200 pb-3 mb-3">
                  <div className="flex h-16 max-w-[180px] shrink-0 items-center justify-center">
                    <img
                      className="max-h-16 w-auto max-w-full object-contain"
                      src={logoSrc}
                      alt=""
                      onError={() => {
                        if (showRemoteLogo) setLogoRemoteFailed(true);
                      }}
                    />
                  </div>
                  <div className="min-w-0 text-center px-1 sm:px-2">
                    {(csp.print_header_center_text || '').trim() ? (
                      <div className="whitespace-pre-wrap text-sm font-medium text-zinc-900">{csp.print_header_center_text}</div>
                    ) : null}
                  </div>
                  <div className="text-right text-[11px] leading-snug text-gray-600 sm:justify-self-end w-full sm:w-auto">
                    <div>{t('Print.DateLabel')}: {printDateStr}</div>
                  </div>
                </header>
                <div className="mb-3 border-t border-gray-200 pt-3">
                  <h1 className="text-3xl font-semibold text-left text-zinc-900">{reportTitle}</h1>
                </div>
              </>
            ) : (
              <div className="flex flex-row justify-between items-center mb-2">
                <div className="flex justify-start items-center">
                  <img className="h-16 w-auto object-contain" src={defaultProductLogoSrc} alt="" />
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-left text-zinc-900">{reportTitle}</h1>
                </div>
              </div>
            )}
            <div className="mt-2">
              <div className="grid grid-cols-[378px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div>
                  <label htmlFor="article_number" className="font-medium text-sm text-gray-800 text-left pr-2 whitespace-nowrap">
                    {t('Cislo reportu')}:
                  </label>
                  <input
                    name="article_number"
                    type="text"
                    value={formErrorRecord.specific_id}
                    readOnly
                    className="text-gray-800 bg-white text-sm text left"
                  />
                </div>

                <div>
                  <div></div>
                  <label htmlFor="created_at" className="font-medium text-sm text-gray-800 pr-2 whitespace-nowrap">
                    {t('Datum')}:
                  </label>
                  <input
                    name="created_at"
                    type="text"
                    value={formatDate(formErrorRecord.created_at, getDateFormat(state.module_settings || []), true)}
                    readOnly
                    className="text-gray-800 bg-white px-1 py-0.5 min-w-0 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D0</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Zakladne udaje')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <label htmlFor="customer_id" className="font-medium text-sm text-gray-800 text-left">
                  {t('Zakaznik')}:
                </label>
                <input
                  name="customer_id"
                  type="text"
                  value={state.companies.filter((c) => c.id === formErrorRecord.customer_id).map((item) => item.name)}
                  readOnly
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <label htmlFor="article_name" className="font-medium text-sm text-gray-800 whitespace-nowrap">
                  {t('Nazov vykresu')}:
                </label>
                <input
                  name="article_name"
                  type="text"
                  value={formErrorRecord.article_name}
                  readOnly
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <label htmlFor="article_number" className="font-medium text-sm text-gray-800 whitespace-nowrap">
                  {t('Cislo vykresu')}:
                </label>
                <input
                  name="article_number"
                  type="text"
                  value={formErrorRecord.article_number}
                  readOnly
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <label htmlFor="claim_counterparty_number" className="font-medium text-sm text-gray-800 whitespace-nowrap">
                  {t('Cislo reklamacie')}:
                </label>
                <input
                  name="claim_counterparty_number"
                  type="text"
                  value={formErrorRecord.claim_counterparty_number}
                  readOnly
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <label htmlFor="total_amount" className="font-medium text-sm text-gray-800 whitespace-nowrap">
                  {t('Pocet dodanych dielov')}:
                </label>
                <input
                  name="total_amount"
                  type="number"
                  value={formErrorRecord.total_amount}
                  readOnly
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <label htmlFor="defective_pieces" className="font-medium text-sm text-gray-800 whitespace-nowrap">
                  {t('Pocet zlych dielov')}:
                </label>
                <input
                  name="defective_pieces"
                  type="text"
                  value={formErrorRecord.defective_pieces}
                  readOnly
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D1</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Popis problemu')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <AutoResizeTextArea name="problem_description" value={formErrorRecord?.problem_description} className="col-span-2" />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D2</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Spracovatel reklamacie')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-1">
                  <label className="text-sm">{t('Meno pracovnika')}:</label>
                  <input
                    name="record_processor_id"
                    type="text"
                    value={state.employees.filter((employee) => employee.id === formErrorRecord.record_processor_id).map((item) => item.name)}
                    readOnly={true}
                    className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                  />
                </div>
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-1">
                  <label className="text-sm">{t('Oddelenie')}:</label>
                  <input
                    name="record_processor_id"
                    type="text"
                    value={state.departments.find((department) => department.id === formErrorRecord.record_processor?.department_id)?.defined_id}
                    readOnly={true}
                    className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                  />
                </div>
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D3</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Hlavna pricina')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <AutoResizeTextArea name="main_reason" value={formErrorRecord?.main_reason} className="col-span-2" />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D4</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Stav reklamacie')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <input
                  name="ack_error"
                  type="text"
                  value={t(formErrorRecord.ack_error || 'Nejasná')}
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D5</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Okamzite opatrenie')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <AutoResizeTextArea name="immediate_measure" value={formErrorRecord?.immediate_measure} className="col-span-2" />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D6</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Dlhodobe opatrenie')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <AutoResizeTextArea name="long_term_measure" value={formErrorRecord?.long_term_measure} className="col-span-2" />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D7</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Zabranenie opakovania')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <input
                  name="prevention_id"
                  type="text"
                  value={state.preventions
                    .filter((prevention) => prevention.id === formErrorRecord.prevention_id)
                    .map((item) => item.name[locale])
                    .join(', ')}
                  readOnly={true}
                  className="text-gray-800 bg-white py-0.5 text-sm w-full text-left"
                />
              </div>
              <div className="grid grid-cols-[25px_203px_1fr] my-0.5 bg-[#d3d3d3] whitespace-nowrap items-center">
                <div className="px-1 py-0.5 font-medium text-sm">D8</div>
                <label className="font-medium text-sm text-gray-800 px-1">{t('Konecna kontrola')}:</label>
                <div></div>
              </div>
              <div className="grid grid-cols-[25px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div></div>
                <AutoResizeTextArea name="auditor_note" value={formErrorRecord?.auditor_note} className="col-span-2" />
              </div>
              <div className="h-12"></div>
              <div className="grid grid-cols-[378px_350px_1fr] gap-x-1 gap-y-1 items-center">
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-1">
                  <label className="text-sm">{t('Skontroloval')}:</label>
                  <input
                    name="auditor_id"
                    type="text"
                    value={state.employees.filter((employee) => employee.id === formErrorRecord.auditor_id).map((item) => item.name)}
                    readOnly={true}
                    className="text-gray-800 bg-white px-1 py-0.5 min-w-0 text-sm focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                </div>
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-1">
                  <label className="text-sm">{t('Oddelenie')}:</label>
                  <input
                    name="auditor_id"
                    type="text"
                    value={state.departments.find((department) => department.id === formErrorRecord.auditor?.department_id)?.defined_id}
                    readOnly={true}
                    className="text-gray-800 bg-white px-1 py-0.5 min-w-0 text-sm focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                  />
                </div>
              </div>
            </div>
            <footer className="mt-8 pt-4 border-t border-gray-200">
              {useUnifiedFooter && csp ? (
                <>
                  <div className="mb-2 space-y-0.5 text-left text-[11px] leading-snug text-zinc-900">
                    <div>
                      <span className="font-semibold">{footerCompany.companyName || '—'}</span>
                      {footerCompany.location ? <span>, {footerCompany.location}</span> : null}
                    </div>
                    {footerCompany.taxLine ? <div>{footerCompany.taxLine}</div> : null}
                    {footerCompany.contactLine ? <div>{footerCompany.contactLine}</div> : null}
                    {footerCompany.bankLine ? <div>{footerCompany.bankLine}</div> : null}
                  </div>
                  {footerUnifiedBody ? (
                    <p className="text-gray-800 text-xs leading-snug whitespace-pre-wrap mb-2">{footerUnifiedBody}</p>
                  ) : null}
                  {csp.show_page_number ? (
                    <div className="flex justify-end text-[11px] text-gray-500">
                      <span>Strana 1 / 1</span>
                    </div>
                  ) : null}
                </>
              ) : hasCspFooterContent ? (
                <>
                  <div className="mb-2 space-y-0.5 text-left text-[11px] leading-snug text-zinc-900">
                    <div>
                      <span className="font-semibold">{footerCompany.companyName || '—'}</span>
                      {footerCompany.location ? <span>, {footerCompany.location}</span> : null}
                    </div>
                    {footerCompany.taxLine ? <div>{footerCompany.taxLine}</div> : null}
                    {footerCompany.contactLine ? <div>{footerCompany.contactLine}</div> : null}
                    {footerCompany.bankLine ? <div>{footerCompany.bankLine}</div> : null}
                  </div>
                  {footerPlainWhenNotUnified ? (
                    <p className="text-gray-800 text-xs leading-snug whitespace-pre-wrap mb-2">{footerPlainWhenNotUnified}</p>
                  ) : null}
                </>
              ) : (
                <p className="text-gray-800 text-xs leading-snug">{t('EightDReport.DefaultProductFooter')}</p>
              )}
            </footer>
          </div>
        </main>
      </div>
    </>
  );
}
